import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { JobSharedAttachments } from "../components/JobSharedAttachments";
import { JobQualityCheck } from "../components/JobQualityCheck";
import {
  Card,
  ErrorPanel,
  Loading,
  PageHeader,
} from "../components/ResourceView";
import {
  fieldCrewApi,
  type FieldJobAssignment,
  type FieldJobOrderItem,
} from "../lib/fieldCrew";
import { useRpcResource } from "../lib/useRpcResource";

const label = (v: string | null | undefined) =>
  v ? String(v).replaceAll("_", " ") : "—";
const dt = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleString() : "—";
function phaseName(code: string | null, type: string | null) {
  if (code === "pickup_teardown" || type === "teardown")
    return "Pickup / Teardown";
  if (code === "receiving_inspection" || type === "inspection")
    return "Receiving / Inspection";
  const names: Record<string, string> = {
    warehouse_prep: "Warehouse Prep",
    loading: "Loading",
    delivery: "Delivery",
    installation: "Installation",
    on_rent: "On Rent",
    return_transport: "Return Transport",
    service_call: "Service Call",
  };
  return names[type ?? ""] ?? label(type);
}

export function FieldJobDetailPage() {
  const { jobId } = useParams();
  const location = useLocation();
  const national = location.pathname.startsWith("/national/");
  const r = useRpcResource(
    () =>
      jobId
        ? fieldCrewApi.jobDetail(jobId)
        : Promise.resolve({
            data: null,
            error: { message: "Job id is missing." },
          }),
    [jobId],
  );
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [attachmentsVersion, setAttachmentsVersion] = useState(0);
  async function completeTask(task: FieldJobAssignment) {
    if (!task.can_complete || busy) return;
    const scrollTop = window.scrollY;
    setBusy(true);
    setActionError(null);
    setNotice(null);
    const x = await fieldCrewApi.completeAssignedPhase(task.assignment_id);
    if (x.error) setActionError(x.error.message);
    else {
      setNotice(
        `${phaseName(task.phase_code, task.phase_type)} marked complete.`,
      );
      await r.reload();
      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          window.scrollTo({ top: scrollTop, behavior: "auto" }),
        ),
      );
    }
    setBusy(false);
  }
  if (r.loading) return <Loading />;
  if (r.error)
    return (
      <>
        <PageHeader
          title={
            national ? "National Field Operations" : "Local Field Operations"
          }
        />
        <ErrorPanel message={r.error} />
      </>
    );
  if (!r.data) return <ErrorPanel message="Field job did not return data." />;
  const d = r.data,
    j = d.job,
    completed = d.field_assignments.filter(
      (x) => x.phase_status === "completed",
    ).length,
    expected = national ? "national" : "local_event";
  if (j.operations_division !== expected)
    return (
      <>
        <PageHeader title="Field Operations" />
        <ErrorPanel
          message={`This is a ${label(j.operations_division)} Job. Open it from the matching Field Operations workspace.`}
        />
      </>
    );
  return (
    <>
      <PageHeader
        title={j.job_name}
        subtitle={`${national ? "National Field Operations" : "Local Field Operations"} · ${j.customer_name ?? "Customer"} · ${j.job_number}${j.order_number ? ` · Order ${j.order_number}` : ""}`}
        actions={
          <>
            <Link
              className="button-link"
              to={national ? "/national/field" : "/field/crew"}
            >
              Back to Assigned Jobs
            </Link>
            <Link className="button-link" to="/time/timecards">
              Crew Timecards
            </Link>
            <button onClick={() => void r.reload()}>Refresh</button>
          </>
        }
      />
      {actionError && <div className="notice notice-danger">{actionError}</div>}
      {notice && <div className="notice notice-success">{notice}</div>}
      <div className="metric-grid four">
        <Metric label="Job status" value={label(j.status)} />
        <Metric label="Dispatch" value={label(j.dispatch_status)} />
        <Metric label="Priority" value={label(j.priority)} />
        <Metric
          label="Tasks complete"
          value={`${completed} of ${d.field_assignments.length}`}
        />
      </div>
      <div className="detail-grid field-job-detail-grid">
        <Card title="Customer & install location">
          <dl className="detail-list">
            <dt>Customer</dt>
            <dd>{j.customer_name ?? "—"}</dd>
            <dt>Site</dt>
            <dd>{j.site_name ?? "—"}</dd>
            <dt>Address</dt>
            <dd>
              {[
                j.site_address_line1,
                j.site_address_line2,
                j.site_city,
                j.site_state_region,
                j.site_postal_code,
              ]
                .filter(Boolean)
                .join(", ") || "—"}
            </dd>
            <dt>Site contact</dt>
            <dd>
              {j.site_contact_name ?? "—"}
              {j.site_contact_phone ? ` · ${j.site_contact_phone}` : ""}
            </dd>
            <dt>Site access</dt>
            <dd>{j.site_access_notes ?? "—"}</dd>
            <dt>Install requirements</dt>
            <dd>{j.install_requirements ?? "—"}</dd>
            <dt>Safety notes</dt>
            <dd>{j.safety_notes ?? "—"}</dd>
          </dl>
        </Card>
        <Card title="Schedule">
          <dl className="detail-list">
            <dt>Rental</dt>
            <dd>
              {j.rental_start_date ?? "—"} → {j.rental_end_date ?? "—"}
            </dd>
            <dt>Delivery</dt>
            <dd>
              {dt(j.delivery_window_start)} → {dt(j.delivery_window_end)}
            </dd>
            <dt>Install</dt>
            <dd>
              {dt(j.install_window_start)} → {dt(j.install_window_end)}
            </dd>
            <dt>Pickup / teardown</dt>
            <dd>
              {dt(j.pickup_window_start)} → {dt(j.pickup_window_end)}
            </dd>
          </dl>
        </Card>
      </div>
      <Card title={`${national ? "National" : "Local"} Field Task Checklist`}>
        <div className="field-task-heading">
          <div>
            <strong>
              {completed} of {d.field_assignments.length} assigned tasks
              complete
            </strong>
            <p className="muted">
              Dispatch assignments appear here automatically. Field Operations
              supervisors can check off a phase assigned to the crew; crew and
              employee schedules remain protected from double booking.
            </p>
          </div>
          <span
            className={`status-pill ${completed === d.field_assignments.length && completed > 0 ? "status-passed" : "status-open"}`}
          >
            {completed === d.field_assignments.length && completed > 0
              ? "All Complete"
              : "In Progress"}
          </span>
        </div>
        {d.field_assignments.length === 0 ? (
          <div className="empty-state">
            Dispatch has not assigned a crew task to this Job yet.
          </div>
        ) : (
          <div className="field-task-list">
            {d.field_assignments.map((x, index) => {
              const previous = d.field_assignments[index - 1];
              const next = d.field_assignments[index + 1];
              const firstTeardown =
                ["pickup", "teardown"].includes(x.phase_type ?? "") &&
                !["pickup", "teardown"].includes(previous?.phase_type ?? "");
              const lastInstallation =
                x.phase_type === "installation" &&
                next?.phase_type !== "installation";
              return (
                <div className="field-task-group" key={x.assignment_id}>
                  {firstTeardown && (
                    <JobQualityCheck
                      jobId={j.id}
                      checkpoint="pre_teardown"
                      onChanged={async () => {
                        setAttachmentsVersion((v) => v + 1);
                        await r.reload();
                      }}
                    />
                  )}
                  <FieldTask
                    task={x}
                    busy={busy}
                    onComplete={completeTask}
                  />
                  {lastInstallation && (
                    <JobQualityCheck
                      jobId={j.id}
                      checkpoint="post_install"
                      onChanged={async () => {
                        setAttachmentsVersion((v) => v + 1);
                        await r.reload();
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
      <Card title="What the crew is installing / Complete BOM">
        {d.order_items.length === 0 ? (
          <div className="empty-state">
            No Order items are attached to this Job.
          </div>
        ) : (
          <div className="field-order-scope">
            {d.order_items.map((x) => (
              <OrderItem item={x} key={x.line_key} />
            ))}
          </div>
        )}
      </Card>
      <JobSharedAttachments jobId={j.id} refreshKey={attachmentsVersion} />
      {d.contacts.length > 0 && (
        <Card title="Job contacts">
          <div className="stack-list">
            {d.contacts.map((c) => (
              <div className="list-row" key={c.id}>
                <div>
                  <strong>{c.name ?? "Contact"}</strong>
                  <span>
                    {label(c.role)}
                    {c.phone ? ` · ${c.phone}` : ""}
                    {c.email ? ` · ${c.email}` : ""}
                  </span>
                </div>
                {c.is_primary && (
                  <span className="status-pill status-current">Primary</span>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

function FieldTask({
  task,
  busy,
  onComplete,
}: {
  task: FieldJobAssignment;
  busy: boolean;
  onComplete: (task: FieldJobAssignment) => Promise<void>;
}) {
  const done = task.phase_status === "completed";
  return (
    <section className={`field-task-card${done ? " complete" : ""}`}>
      <span className={`ops-check ${done ? "done" : ""}`}>
        {done ? "✓" : ""}
      </span>
      <div className="field-task-main">
        <strong>{phaseName(task.phase_code, task.phase_type)}</strong>
        <span>{task.crew_name}</span>
        <small>
          {dt(task.starts_at)} → {dt(task.ends_at)}
        </small>
      </div>
      <span className={`status-pill status-${task.phase_status ?? "assigned"}`}>
        {done ? "Complete" : label(task.phase_status ?? "assigned")}
      </span>
      {!done && task.can_complete && (
        <button
          className="primary"
          disabled={busy}
          onClick={() => void onComplete(task)}
        >
          Mark Complete
        </button>
      )}
      {!done && !task.can_complete && (
        <small className="field-task-permission">
          {task.completion_blocker ?? "Field supervisor completion required"}
        </small>
      )}
    </section>
  );
}

function OrderItem({ item }: { item: FieldJobOrderItem }) {
  const cfg = item.configuration_snapshot ?? {},
    width = Number(cfg.width_m ?? 0),
    length = Number(cfg.length_m ?? 0),
    components = item.bom_components ?? [];
  const total = (q: number | null | undefined) =>
    Number(q ?? 0) * Number(item.quantity || 1);
  return (
    <section className="field-order-item">
      <div>
        <span className="eyebrow">
          {label(item.line_type)}
          {item.sku ? ` · ${item.sku}` : ""}
        </span>
        <strong>{item.product_name ?? item.description}</strong>
        {item.description !== item.product_name && (
          <small>{item.description}</small>
        )}
        {width > 0 && length > 0 && (
          <small>
            {Math.round(width / 0.3048)}' × {Math.round(length / 0.3048)}'
          </small>
        )}
      </div>
      <strong className="field-order-qty">
        Qty {Number(item.quantity).toLocaleString()}
      </strong>
      {item.notes && <p>{item.notes}</p>}
      <details className="field-bom">
        <summary className="field-bom-title">
          <strong>Assembly parts</strong>
          <span>
            {components.length} component{components.length === 1 ? "" : "s"}
          </span>
        </summary>
        {components.length === 0 ? (
          <div className="job-bom-note">
            <strong>BOM not linked</strong>
            <span>
              The accepted Order does not contain component records for this
              item.
            </span>
          </div>
        ) : (
          <div className="field-bom-list">
            {components.map((c, i) => (
              <div
                className="job-bom-row"
                key={`${c.product_id ?? c.sku ?? i}-${c.component_role ?? ""}`}
              >
                <div>
                  <strong>{c.name}</strong>
                  <small>
                    {[
                      c.sku,
                      c.component_role ? label(c.component_role) : null,
                      c.tracking_method ? label(c.tracking_method) : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </small>
                </div>
                <span>
                  Qty{" "}
                  {total(c.required_quantity_per_unit).toLocaleString(
                    undefined,
                    { maximumFractionDigits: 2 },
                  )}
                  {c.unit_of_measure ? ` ${c.unit_of_measure}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </details>
    </section>
  );
}
function Metric({
  label: metricLabel,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="metric">
      <span>{metricLabel}</span>
      <strong>{value}</strong>
    </div>
  );
}
